import { useState } from 'react';

const PROMPT_TEXTS = {
  simple_pleasures: 'My simple pleasures...',
  geek_out: 'I geek out on...',
  life_goal: 'A life goal of mine...',
  unusual_skill: 'My most unusual skill...',
  green_flags: 'My green flags...',
  perfect_sunday: 'My perfect Sunday...',
  deal_breaker: 'My biggest deal breaker...',
  best_travel: 'Best travel story...',
  dinner_guest: 'Dream dinner guest...',
  hot_take: 'My most controversial opinion...',
  love_language: 'My love language is...',
  pet_peeve: 'My biggest pet peeve...',
  guilty_pleasure: 'My guilty pleasure...',
  two_truths_lie: 'Two truths and a lie...',
  typical_sunday: 'You can find me on a typical Sunday...',
  first_thing_noticed: 'First thing people notice about me...',
  never_do_again: 'Something I will never do again...',
  most_spontaneous: 'Most spontaneous thing I have done...',
  believe_in: 'I believe that...',
  change_mind: 'Something that changed my mind...',
};

const TRAIT_LABELS = {
  smoking: 'Smoking', drinking: 'Drinking', cannabis: 'Cannabis', diet: 'Diet',
  exercise: 'Exercise', sleep_schedule: 'Sleep', education: 'Education',
  religion: 'Religion', politics: 'Politics', languages: 'Languages',
  ethnicity: 'Ethnicity', height: 'Height', body_type: 'Body type',
  has_kids: 'Has kids', wants_kids: 'Wants kids', pets: 'Pets',
  communication_style: 'Communication', social_energy: 'Social energy',
  relationship_goal: 'Looking for', love_language_trait: 'Love language',
};

const styles = {
  card: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-lg)',
    touchAction: 'none',
    userSelect: 'none',
  },
  photoContainer: {
    position: 'relative',
    width: '100%',
    height: '65%',
    background: 'var(--bg-elevated)',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noPhoto: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: '64px',
    color: 'var(--text-muted)',
  },
  gradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '120px',
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.65))',
  },
  photoNav: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    gap: '3px',
    padding: '8px 8px 0',
    zIndex: 2,
  },
  photoDot: {
    flex: 1,
    height: '3px',
    borderRadius: '1.5px',
    background: 'rgba(255,255,255,0.3)',
  },
  photoDotActive: {
    background: '#fff',
  },
  info: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px 20px',
    zIndex: 2,
  },
  name: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
  },
  meta: {
    display: 'flex',
    gap: '8px',
    marginTop: '6px',
    flexWrap: 'wrap',
  },
  details: {
    height: '35%',
    overflowY: 'auto',
    padding: '16px 20px',
  },
  bio: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '12px',
  },
  promptCard: {
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-sm)',
    padding: '12px',
    marginBottom: '8px',
  },
  promptQ: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    marginBottom: '4px',
  },
  promptA: {
    fontSize: '14px',
    lineHeight: 1.4,
  },
  vaxSection: {
    marginTop: '12px',
  },
  vaxTitle: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  vaxGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  compatBadge: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'rgba(232, 87, 109, 0.9)',
    color: '#fff',
    borderRadius: '20px',
    padding: '4px 12px',
    fontSize: '14px',
    fontWeight: '700',
    zIndex: 3,
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  traitsSection: {
    marginTop: '12px',
  },
  traitsSectionTitle: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  traitChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px',
  },
  traitChip: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '10px',
    background: 'var(--bg-elevated)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
  },
  photoArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.3)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    border: 'none',
    cursor: 'pointer',
    zIndex: 3,
    padding: 0,
  },
  photoArrowLeft: {
    left: '8px',
  },
  photoArrowRight: {
    right: '8px',
  },
  stamp: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%) rotate(-20deg)',
    fontSize: '42px',
    fontWeight: '800',
    padding: '8px 20px',
    border: '4px solid',
    borderRadius: '12px',
    opacity: 0,
    transition: 'opacity 100ms',
    zIndex: 10,
    pointerEvents: 'none',
  },
  stampLike: {
    left: '20px',
    color: 'var(--success)',
    borderColor: 'var(--success)',
  },
  stampPass: {
    right: '20px',
    color: 'var(--danger)',
    borderColor: 'var(--danger)',
    transform: 'translateY(-50%) rotate(20deg)',
  },
};

export default function SwipeCard({ profile, style, dragOffset = 0 }) {
  const [photoIndex, setPhotoIndex] = useState(0);

  if (!profile) return null;

  const photos = profile.photos || [];
  const currentPhoto = photos[photoIndex];

  const handlePhotoTap = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x > rect.width / 2) {
      setPhotoIndex((i) => Math.min(i + 1, photos.length - 1));
    } else {
      setPhotoIndex((i) => Math.max(i - 1, 0));
    }
  };

  const likeOpacity = Math.min(Math.max(dragOffset / 100, 0), 1);
  const passOpacity = Math.min(Math.max(-dragOffset / 100, 0), 1);

  return (
    <div style={{ ...styles.card, ...style }}>
      <div style={styles.photoContainer} onClick={handlePhotoTap}>
        {photos.length > 1 && (
          <div style={styles.photoNav}>
            {photos.map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.photoDot,
                  ...(i === photoIndex ? styles.photoDotActive : {}),
                }}
              />
            ))}
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button
              style={{ ...styles.photoArrow, ...styles.photoArrowLeft }}
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((i) => Math.max(i - 1, 0));
              }}
              aria-label="Previous photo"
            >
              &#8249;
            </button>
            <button
              style={{ ...styles.photoArrow, ...styles.photoArrowRight }}
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex((i) => Math.min(i + 1, photos.length - 1));
              }}
              aria-label="Next photo"
            >
              &#8250;
            </button>
          </>
        )}

        {profile.compatibility_score != null && (
          <div style={styles.compatBadge}>
            <span>♥</span> {Math.round(profile.compatibility_score)}%
          </div>
        )}

        {currentPhoto ? (
          <img
            src={`/uploads/${currentPhoto.filename}`}
            alt={profile.display_name}
            style={styles.photo}
            draggable={false}
          />
        ) : (
          <div style={styles.noPhoto}>?</div>
        )}

        <div style={styles.gradient} />

        <div
          style={{ ...styles.stamp, ...styles.stampLike, opacity: likeOpacity }}
        >
          LIKE
        </div>
        <div
          style={{ ...styles.stamp, ...styles.stampPass, opacity: passOpacity }}
        >
          NOPE
        </div>

        <div style={styles.info}>
          <div style={styles.name}>
            {profile.display_name}
            {profile.age ? `, ${profile.age}` : ''}
          </div>
          <div style={styles.meta}>
            {profile.gender && (
              <span className="badge badge-accent">{profile.gender}</span>
            )}
            {profile.pronouns && (
              <span className="badge badge-muted">{profile.pronouns}</span>
            )}
            {profile.distance != null && (
              <span className="badge badge-muted">
                {profile.distance} mi away
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={styles.details}>
        {profile.bio && <p style={styles.bio}>{profile.bio}</p>}

        {profile.prompts?.map((p) => (
          <div key={p.prompt_key} style={styles.promptCard}>
            <p style={styles.promptQ}>{PROMPT_TEXTS[p.prompt_key] || p.prompt_key}</p>
            <p style={styles.promptA}>{p.response}</p>
          </div>
        ))}

        {profile.traits?.length > 0 && (
          <div style={styles.traitsSection}>
            <p style={styles.traitsSectionTitle}>About them</p>
            <div style={styles.traitChips}>
              {profile.traits.map((t) => {
                let display = t.trait_value;
                try { display = JSON.parse(t.trait_value); } catch {}
                if (Array.isArray(display)) display = display.join(', ');
                return (
                  <span key={t.trait_key} style={styles.traitChip}>
                    {TRAIT_LABELS[t.trait_key] || t.trait_key}: {display}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {profile.preferences?.length > 0 && (
          <div style={styles.traitsSection}>
            <p style={styles.traitsSectionTitle}>Looking for</p>
            <div style={styles.traitChips}>
              {profile.preferences.filter((p) => p.pref_value !== 'No preference').map((p) => {
                let display = p.pref_value;
                try { display = JSON.parse(p.pref_value); } catch {}
                if (Array.isArray(display)) display = display.join(', ');
                return (
                  <span key={p.pref_key} style={styles.traitChip}>
                    {TRAIT_LABELS[p.pref_key] || p.pref_key}: {display}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {profile.vaccinations?.length > 0 && (
          <div style={styles.vaxSection}>
            <p style={styles.vaxTitle}>Vaccination Status</p>
            <div style={styles.vaxGrid}>
              {profile.vaccinations.map((v) => (
                <span
                  key={v.vaccine_type}
                  className={`badge ${v.status === 'vaccinated' ? 'badge-success' : v.status === 'partial' ? 'badge-warning' : 'badge-muted'}`}
                >
                  {v.vaccine_type.replace(/_/g, ' ')}
                  {v.status === 'vaccinated' ? ' ✓' : v.status === 'partial' ? ' ~' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {profile.vaccination_opted_out && (
          <div style={styles.vaxSection}>
            <span className="badge badge-muted">Vaccination status: Opted out</span>
          </div>
        )}
      </div>
    </div>
  );
}
